import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useAuth0User } from '@hooks/useAuth0User';
import { isDemoUser } from '@lib/auth0/utils';
import { DEMO_SESSION_FLAG_OVERRIDES } from '@lib/posthog-feature-flags';
import type { PostHogFeatureFlags } from '@lib/posthog-feature-flags';

/**
 * Read a PostHog boolean feature flag.
 *
 * New feature flags should be added to `PostHogFeatureFlags` in
 * `@lib/posthog-feature-flags` and consumed via this hook. Flags that carry
 * a JSON payload belong with `usePostHogFeatureFlagPayload`.
 *
 * Returns the supplied default while PostHog is still loading flags on the
 * client, and when PostHog is not configured (e.g. local development or
 * e2e runs without the env vars set), so consumers never observe an
 * `undefined` state. Match the kill-switch convention in this codebase
 * (`pointsPageIsClosed`, `fplSeasonIsClosed`...) where the default value
 * renders the working page; tests then exercise the default path without
 * needing to mock the flag service.
 *
 * App-review demo sessions bypass PostHog for the season-dependent flags in
 * `DEMO_SESSION_FLAG_OVERRIDES`, so the demo user sees the mid-season
 * experience their demo backend serves while production traffic keeps the
 * real flag values.
 */
export const usePostHogFeatureFlag = (
  flag: PostHogFeatureFlags,
  defaultValue: boolean
): boolean => {
  const { user } = useAuth0User();
  const value = useFeatureFlagEnabled(flag);

  const demoOverride = DEMO_SESSION_FLAG_OVERRIDES[flag];
  if (demoOverride !== undefined && isDemoUser(user)) {
    return demoOverride;
  }

  return value ?? defaultValue;
};
